import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getCompany } from "../lib/marketplaceApi";
import type { PublicCompany } from "../lib/marketplaceApi";
import { getSession } from "../lib/auth";
import { absoluteUrl, breadcrumbLd, useSeo } from "../lib/seo";
import { cn, longDate } from "../lib/format";
import { label, useT } from "../i18n";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import Badge from "../components/ui/Badge";
import VehicleCardItem from "../components/home/VehicleCardItem";

type SortKey = "default" | "price-asc" | "price-desc";

function Stars({ value, className }: { value: number; className?: string }) {
  const full = Math.round(value);
  return (
    <span className={cn("tracking-[1px]", className)} aria-label={`${value}/5`}>
      <span className="text-ink">{"★".repeat(full)}</span>
      <span className="text-[#D4D4D4]">{"★".repeat(5 - full)}</span>
    </span>
  );
}

/** /companies/:publicId — public page of a rental company (GET /companies/:publicId). */
export default function CompanyPublic() {
  const t = useT();
  const { publicId = "" } = useParams();
  const [state, setState] = useState<{ id: string; company: PublicCompany | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCompany(publicId)
      .then((company) => !cancelled && setState({ id: publicId, company }))
      .catch(() => !cancelled && setState({ id: publicId, company: null }));
    return () => {
      cancelled = true;
    };
  }, [publicId]);

  const loaded = state?.id === publicId ? state : null;

  // AutoRental business with its drivers' rating and latest reviews
  const company = loaded?.company ?? null;
  const companyName = company ? t.common.companyLabel(company.publicId) : "";
  useSeo(
    !loaded
      ? null
      : !company
        ? { title: t.companyPage.notFoundTitle, noindex: true }
        : {
            title: t.seo.company.title(companyName),
            description: t.seo.company.description(companyName, company.stats.listed, company.area),
            path: `/companies/${company.publicId}`,
            type: "profile",
            jsonLd: [
              {
                "@context": "https://schema.org",
                "@type": "AutoRental",
                name: companyName,
                url: absoluteUrl(`/companies/${company.publicId}`),
                ...(company.area && { areaServed: company.area }),
                ...(company.memberSince && { foundingDate: String(company.memberSince) }),
                ...(company.rating.count > 0 && {
                  aggregateRating: { "@type": "AggregateRating", ratingValue: company.rating.average, reviewCount: company.rating.count, bestRating: 5, worstRating: 1 },
                  review: company.reviews.slice(0, 5).map((r) => ({
                    "@type": "Review",
                    author: { "@type": "Person", name: r.author },
                    reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
                    ...(r.text && { reviewBody: r.text }),
                    ...(r.date && { datePublished: r.date.slice(0, 10) }),
                  })),
                }),
                makesOffer: company.listings.slice(0, 20).map((v) => ({
                  "@type": "Offer",
                  url: absoluteUrl(`/vehicles/${v.slug}`),
                  price: v.weekly,
                  priceCurrency: "EUR",
                  itemOffered: { "@type": "Car", name: `${v.brand} ${v.model}` },
                })),
              },
              breadcrumbLd([[t.seo.breadcrumbHome, "/"], [t.seo.breadcrumbVehicles, "/vehicles"], [companyName]]),
            ],
          },
  );

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header activeHref="/vehicles" />
      <main className="mx-auto w-full max-w-300 flex-1 px-4 pt-4 pb-16 sm:px-6">
        {!loaded ? (
          <div aria-busy="true">
            <div className="mt-6 h-16 w-80 animate-pulse rounded bg-surface" />
            <div className="mt-6 h-40 animate-pulse rounded-lg bg-surface" />
          </div>
        ) : !loaded.company ? (
          <div className="py-20 text-center">
            <h1 className="m-0 mb-2 text-2xl font-medium">{t.companyPage.notFoundTitle}</h1>
            <p className="m-0 mb-5 text-sm text-muted">{t.companyPage.notFoundText}</p>
            <Link to="/vehicles" className="text-sm text-brand hover:text-brand-link">
              {t.vehicle.seeAll}
            </Link>
          </div>
        ) : (
          <CompanyView company={loaded.company} />
        )}
      </main>
      <Footer />
    </div>
  );
}

function CompanyView({ company: c }: { company: PublicCompany }) {
  const t = useT();
  const p = t.companyPage;
  const companyLabel = t.common.companyLabel(c.publicId);
  const title = c.details?.name ?? companyLabel;
  const [energy, setEnergy] = useState<string>("all");
  const [availableOnly, setAvailableOnly] = useState(false);
  const [sort, setSort] = useState<SortKey>("default");

  const energies = [...new Set(c.listings.map((l) => l.energy))];
  const shown = c.listings
    .filter((l) => (energy === "all" || l.energy === energy) && (!availableOnly || l.availableUnits > 0))
    .sort((a, b) => (sort === "price-asc" ? a.weekly - b.weekly : sort === "price-desc" ? b.weekly - a.weekly : 0));

  const locked = (
    <Link to="/login" className="inline-flex items-center gap-1 text-[13px] text-brand-link no-underline hover:underline">
      <span aria-hidden="true">🔒</span> {p.locked}
    </Link>
  );
  const info: [string, React.ReactNode][] = [
    [p.publicId, <span className="font-mono text-[13px]">{companyLabel}</span>],
    [p.area, c.area || "—"],
    [p.name, c.details ? c.details.name : locked],
    [p.address, c.details ? c.details.address || "—" : locked],
    [p.contact, c.details ? `${c.details.phone} · ${c.details.email}` : locked],
    ...(c.memberSince ? ([[p.memberSinceLabel, String(c.memberSince)]] as [string, React.ReactNode][]) : []),
  ];

  return (
    <>
      <nav className="text-xs text-muted">
        <Link to="/vehicles" className="text-muted hover:text-ink">
          {p.breadcrumb}
        </Link>{" "}
        / {companyLabel}
      </nav>

      {/* Header */}
      <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-line-strong pb-6">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-ink text-sm font-semibold text-white">
          #{c.publicId}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="m-0 text-2xl font-medium sm:text-[28px]">{title}</h1>
            {c.verified && (
              <Badge tone="green" className="text-[11px]">
                ✓ {t.common.verifiedCompany}
              </Badge>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-muted">
            <span>{p.ownerType[c.ownerType]}</span>
            {c.area && <span>{c.area}</span>}
            {c.memberSince && <span>{p.memberSince(c.memberSince)}</span>}
            {c.rating.count > 0 ? (
              <a href="#reviews" className="text-ink no-underline">
                <Stars value={c.rating.average} className="text-xs" />{" "}
                <span className="underline">
                  {c.rating.average} · {p.reviewsCount(c.rating.count)}
                </span>
              </a>
            ) : (
              <span>{p.noReviews}</span>
            )}
          </div>
        </div>
        {c.listings.length > 0 && (
          <a
            href="#vehicles"
            className="inline-flex h-10 items-center rounded bg-brand px-4.5 text-[13px] font-medium text-white no-underline hover:bg-brand-dark"
          >
            {p.viewAll(c.listings.length)}
          </a>
        )}
      </div>

      {/* About + information */}
      <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div>
          <h2 className="m-0 mb-3 text-lg font-medium">{p.about}</h2>
          {!c.details && (
            <div className="flex flex-wrap items-center gap-4 rounded-lg bg-surface p-4">
              <p className="m-0 min-w-55 flex-1 text-[13px] leading-normal text-ink-soft">{p.lockedText}</p>
              {!getSession() && (
                <Link
                  to="/signup"
                  className="inline-flex h-9 items-center rounded bg-ink px-4 text-[13px] font-medium text-white no-underline"
                >
                  {p.unlock}
                </Link>
              )}
            </div>
          )}
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              [c.stats.listed, p.stats.listed],
              [c.stats.available, p.stats.available],
              [c.stats.fleet, p.stats.fleet],
              [c.rating.count ? c.rating.average : "—", c.rating.count ? p.reviewsCount(c.rating.count) : p.noReviews],
            ].map(([value, text]) => (
              <div key={String(text)} className="rounded-lg bg-surface p-4">
                <div className="text-2xl font-medium">{value}</div>
                <div className="mt-0.5 text-xs text-muted">{text}</div>
              </div>
            ))}
          </div>
        </div>

        <aside className="rounded-lg border border-line-strong p-4">
          <h2 className="m-0 mb-3 text-sm font-medium">{p.infoTitle}</h2>
          <dl className="m-0 grid gap-3">
            {info.map(([term, value]) => (
              <div key={term}>
                <dt className="text-[11px] text-muted">{term}</dt>
                <dd className="m-0 mt-0.5 text-[13px]">{value}</dd>
              </div>
            ))}
          </dl>
        </aside>
      </div>

      {/* Vehicles */}
      <section id="vehicles" className="mt-10 scroll-mt-20">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="m-0 text-lg font-medium">{p.vehiclesTitle(title)}</h2>
            <p className="m-0 mt-0.5 text-xs text-muted">{p.vehiclesSub(c.stats.listed, c.stats.available)}</p>
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            aria-label={t.vehicles.sort}
            className="h-8 rounded border-0 bg-surface px-2.5 text-xs text-ink outline-none"
          >
            {(Object.keys(p.sort) as SortKey[]).map((k) => (
              <option key={k} value={k}>
                {p.sort[k]}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {["all", ...energies].map((e) => {
            const n = e === "all" ? c.listings.length : c.listings.filter((l) => l.energy === e).length;
            return (
              <button
                key={e}
                type="button"
                onClick={() => setEnergy(e)}
                aria-pressed={energy === e}
                className={cn(
                  "h-8 cursor-pointer rounded-full border px-3 text-xs font-medium",
                  energy === e ? "border-ink bg-ink text-white" : "border-line-strong bg-white text-ink hover:bg-surface",
                )}
              >
                {e === "all" ? p.all : label(t.enums.energy, e)} ({n})
              </button>
            );
          })}
          <label className="ml-2 flex cursor-pointer items-center gap-1.5 text-xs text-ink-soft">
            <input type="checkbox" checked={availableOnly} onChange={(e) => setAvailableOnly(e.target.checked)} className="accent-brand" />
            {p.availableOnly}
          </label>
        </div>

        {shown.length ? (
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {shown.map((v) => (
              <VehicleCardItem key={v.id} vehicle={v} hideCompany />
            ))}
          </div>
        ) : (
          <p className="mt-4 rounded-lg bg-surface px-6 py-10 text-center text-sm text-muted">{p.noVehicles}</p>
        )}
      </section>

      {/* Reviews */}
      <section id="reviews" className="mt-12 scroll-mt-20">
        <h2 className="m-0 mb-4 text-lg font-medium">{p.reviewsTitle}</h2>
        {c.rating.count === 0 ? (
          <p className="m-0 rounded-lg bg-surface px-6 py-10 text-center text-sm text-muted">{p.noReviewsText}</p>
        ) : (
          <div className="grid items-start gap-6 md:grid-cols-[260px_minmax(0,1fr)]">
            <div className="rounded-lg border border-line-strong p-4">
              <div className="flex items-baseline gap-2.5">
                <span className="text-4xl font-medium">{c.rating.average}</span>
                <span>
                  <Stars value={c.rating.average} className="text-sm" />
                  <span className="block text-xs text-muted">{p.reviewsCount(c.rating.count)}</span>
                </span>
              </div>
              <div className="mt-3 grid gap-1.5">
                {([5, 4, 3, 2, 1] as const).map((star) => {
                  const n = c.rating.distribution[star] ?? 0;
                  return (
                    <div key={star} className="flex items-center gap-2 text-xs">
                      <span className="w-6 shrink-0">{star} ★</span>
                      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface">
                        <span className="block h-full bg-ink" style={{ width: `${(n / c.rating.count) * 100}%` }} />
                      </span>
                      <span className="w-5 shrink-0 text-right text-muted">{n}</span>
                    </div>
                  );
                })}
              </div>
              <p className="m-0 mt-4 text-[11px] leading-normal text-faint">{p.reviewsNote}</p>
            </div>

            <ul className="m-0 list-none border-t border-line-strong p-0">
              {c.reviews.map((r) => (
                <li key={r.id} className="border-b border-line-strong py-4">
                  <div className="flex flex-wrap items-center gap-2 text-[13px]">
                    <b className="font-medium">{r.author}</b>
                    <Stars value={r.rating} className="text-xs" />
                    <span className="text-xs text-muted">{longDate(r.date.slice(0, 10))}</span>
                  </div>
                  {r.vehicle && <div className="mt-0.5 text-[11px] text-muted">{p.rented(r.vehicle)}</div>}
                  <p className="m-0 mt-2 whitespace-pre-line text-[13px] leading-relaxed text-ink-soft">{r.text}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </>
  );
}
